import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import type { LayerState, VectorLayerState } from '@tmapy/beetlejuice-utils';
import { GEOMETRY_TYPE, INTERACTION } from '@tmapy/beetlejuice-utils';
import { useDispatch, useSelector } from '@tmapy/redux';
import { SvgSelect, SvgPen, SecondaryBtn } from '@tmapy/style-guide';
import { DeleteSelectionBtn, DeleteAllBtn, actionSetInteraction } from '@tmapy/mapcore';

import { UNMANAGED_AREA_LAYERS } from '../constants';

const msg = defineMessages({
  polygonDrawBtn: { id: 'drawBtn.polygon.label', defaultMessage: 'Polygon' },
  boxDrawBtn: { id: 'drawBtn.box.label', defaultMessage: 'Obdélník' },
  selectBtn: { id: 'selectBtn.label', defaultMessage: 'Vybrat' },
  deleteAllBtn: {
    id: 'deleteAllBtn.label',
    defaultMessage: 'Smazat vše',
  },
  deleteSelectionBtn: {
    id: 'deleteSelectionBtn.label',
    defaultMessage: 'Smazat vybrané',
  },
});

const drawInteraction = {
  type: INTERACTION.DRAW,
  layerId: UNMANAGED_AREA_LAYERS.CUSTOM_AREAS,
  drawGeometryType: GEOMETRY_TYPE.MULTI_POLYGON,
  style: undefined,
};
const drawBoxInteraction = {
  type: INTERACTION.DRAW,
  layerId: UNMANAGED_AREA_LAYERS.CUSTOM_AREAS,
  drawGeometryType: GEOMETRY_TYPE.BOX,
  style: undefined,
};

export const Edit: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    type: interactionType,
    layerId: interactionLayerId,
    selectedFeatureIds,
    drawGeometryType,
  } = useSelector((state) => state.mapCore.interaction);

  const layers = useSelector((state) => state.mapCore.layers);
  const editMapFeatures =
    (
      layers.find(
        (layer: LayerState) => layer.id === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS,
      ) as VectorLayerState
    )?.features ?? [];
  const hasDrawnFeatures = !!editMapFeatures.length;

  const handleDrawBtnClick = useCallback(() => {
    if (
      interactionType === INTERACTION.DRAW &&
      drawGeometryType === GEOMETRY_TYPE.MULTI_POLYGON &&
      interactionLayerId === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS
    ) {
      dispatch(
        actionSetInteraction({
          type: INTERACTION.NONE,
        }),
      );
    } else {
      dispatch(actionSetInteraction(drawInteraction));
    }
  }, [dispatch, interactionLayerId, interactionType, drawGeometryType]);

  const handleDrawBoxBtnClick = useCallback(() => {
    if (
      interactionType === INTERACTION.DRAW &&
      drawGeometryType === GEOMETRY_TYPE.BOX &&
      interactionLayerId === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS
    ) {
      dispatch(
        actionSetInteraction({
          type: INTERACTION.NONE,
        }),
      );
    } else {
      dispatch(actionSetInteraction(drawBoxInteraction));
    }
  }, [dispatch, interactionLayerId, interactionType, drawGeometryType]);

  const handleSelectBtnClick = useCallback(() => {
    if (interactionType === INTERACTION.SELECT) {
      dispatch(
        actionSetInteraction({
          type: INTERACTION.NONE,
        }),
      );
    } else {
      dispatch(
        actionSetInteraction({
          type: INTERACTION.SELECT,
          layerId: UNMANAGED_AREA_LAYERS.CUSTOM_AREAS,
        }),
      );
    }
  }, [dispatch, interactionType]);

  useEffect(() => {
    dispatch(actionSetInteraction(drawInteraction));
    return () => {
      dispatch(
        actionSetInteraction({
          type: INTERACTION.NONE,
        }),
      );
    };
  }, []);

  return (
    <>
      <SecondaryBtn
        icon={{ element: <SvgPen /> }}
        size="sml"
        isActive={
          interactionType === INTERACTION.DRAW &&
          drawGeometryType === GEOMETRY_TYPE.MULTI_POLYGON &&
          interactionLayerId === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS
        }
        onClick={handleDrawBtnClick}
      >
        {intl.formatMessage(msg.polygonDrawBtn)}
      </SecondaryBtn>
      <SecondaryBtn
        icon={{ element: <SvgPen /> }}
        size="sml"
        isActive={
          interactionType === INTERACTION.DRAW &&
          drawGeometryType === GEOMETRY_TYPE.BOX &&
          interactionLayerId === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS
        }
        onClick={handleDrawBoxBtnClick}
      >
        {intl.formatMessage(msg.boxDrawBtn)}
      </SecondaryBtn>
      <SecondaryBtn
        icon={{ element: <SvgSelect /> }}
        size="sml"
        isActive={interactionType === INTERACTION.SELECT}
        isDisabled={!hasDrawnFeatures}
        onClick={handleSelectBtnClick}
      >
        {intl.formatMessage(msg.selectBtn)}
      </SecondaryBtn>
      <DeleteSelectionBtn
        isDisabled={!selectedFeatureIds.length}
        dispatch={dispatch}
        layerId={UNMANAGED_AREA_LAYERS.CUSTOM_AREAS}
        featureIds={selectedFeatureIds}
      >
        {intl.formatMessage(msg.deleteSelectionBtn)}
      </DeleteSelectionBtn>
      <DeleteAllBtn
        isDisabled={!hasDrawnFeatures}
        dispatch={dispatch}
        layerId={UNMANAGED_AREA_LAYERS.CUSTOM_AREAS}
      >
        {intl.formatMessage(msg.deleteAllBtn)}
      </DeleteAllBtn>
    </>
  );
};
